/**
 * The camera universe, fetched on first use from the published asset
 * assets/data.json (js/build-data.js merges it from resources/cameras.json --
 * the same file the server reads -- plus photos.json and photo-credits.json).
 * Changes about once a year; refresh via `php flarum cameras:update`, rebuild,
 * then `php flarum assets:publish`.
 *
 * It used to be three build-time `import`s, which put ~295 KB of JSON into
 * forum.js for every visitor on every page. Nothing here is needed until the
 * user opens /cameras, the composer picker, or hovers a camera link, so it is
 * loaded on demand instead.
 *
 * The bindings below are deliberately `let`: ES module live bindings mean the
 * eight consumers keep importing { cameras } / { byId } and see the real data
 * the moment load() resolves. Only the entry points had to learn to wait.
 */
import app from 'flarum/forum/app';

export let meta = null;

export let cameras = [];

export let byId = {};

export let loaded = false;

let photos = {};
let credits = {};
let promise = null;

/**
 * Fetch the camera data once. Safe to call from anywhere, any number of
 * times -- every caller gets the same promise.
 */
export function load() {
  if (!promise) {
    const base = app.forum.attribute('basePath') + '/assets/extensions/acseven-chdk-cameras/';
    // The version query busts both the browser and Cloudflare caches when
    // cameras:update rewrites the file; the asset itself is served with a
    // far-future max-age like every other published asset.
    const url = base + 'data.json?v=' + app.forum.attribute('chdkCamerasDataVersion');

    promise = fetch(url)
      .then((r) => r.json())
      .then((d) => {
        meta = d;
        cameras = d.cameras;
        byId = Object.fromEntries(cameras.map((c) => [c.id, c]));
        // product lines in first-seen order (A, D, G, IXUS, M, N, S, SX, TX).
        // Deliberately no Set: Flarum's Babel preset compiles [...new Set(x)]
        // to [].concat(set), which appends the Set as ONE element instead of
        // iterating it, so `lines` became [Set] and every group on the index
        // rendered empty.
        lines = cameras.map((c) => c.line).filter((line, i, all) => all.indexOf(line) === i);
        photos = d.photos || {};
        credits = d.credits || {};
        loaded = true;
      })
      // ponytail: a 503 or an HTML 404 body (assets:publish not run yet) must
      // not stay cached as a rejected promise for the rest of the session --
      // forget it so the next hover / page visit retries. No error UI.
      .catch((e) => {
        promise = null;
        throw e;
      });
  }

  return promise;
}

// Which cameras have a photo. Staff uploads (runtime overrides, served from
// photos/uploaded/) win over the published Wikimedia thumbs
// (resources/photos.json from scripts/fetch-photos.php); the rest get the
// fallback plate.
const overrides = () => app.forum.attribute('chdkCamerasPhotoOverrides') || {};

export const photoFile = (camera) => overrides()[camera.id] || photos[camera.id] || null;

export function photoUrl(camera) {
  const base = app.forum.attribute('basePath') + '/assets/extensions/acseven-chdk-cameras/photos/';
  const up = overrides()[camera.id];

  if (up) return base + 'uploaded/' + camera.id + '.jpg?v=' + up.v; // v busts the cache on replace
  return base + photos[camera.id];
}

// Author + license for the published Commons photo; null when a staff upload
// is in effect or the camera has no photo. Rendered in the photo modal --
// we redistribute local copies, so the attribution must ship with them.
export function photoCredit(camera) {
  if (overrides()[camera.id]) return null;
  return credits[camera.id] || null;
}

// assigned by load(); see the note there about why this is not a Set
export let lines = [];

export function firmwareCount(camera) {
  return camera.firmware.length;
}

export function totalDownloads(camera) {
  return camera.firmware.reduce((n, fw) => n + (fw.full ? 1 : 0) + (fw.small ? 1 : 0), 0);
}

export function matchesCamera(camera, q) {
  q = q.trim().toLowerCase();
  if (!q) return true;
  return (
    camera.name.toLowerCase().includes(q) ||
    camera.id.includes(q) ||
    camera.aka.some((a) => a.toLowerCase().includes(q)) ||
    camera.match.some((k) => k.toLowerCase().includes(q))
  );
}

/**
 * Release changelog entries that mention this camera: an entry counts if any
 * part of the platform id (ixus115_elph100hs -> ixus115 / elph100hs) appears
 * as a word in the commit message.
 */
export function changelogFor(camera) {
  const parts = camera.id.split('_').map((p) => p.replace(/[^a-z0-9]/gi, ''));
  const patterns = parts.map((p) => new RegExp(`(^|[^a-z0-9])${p}([^a-z0-9]|$)`, 'i'));

  return ((meta && meta.changelog) || []).filter((entry) => {
    const msg = entry.msg || '';
    return patterns.some((re) => re.test(msg));
  });
}

/**
 * The camera's CHDK wiki page, or null when it has none. Resolved once by
 * scripts/fetch-wiki-titles.php: deriving it from the name (`SX510 HS` ->
 * `SX510_HS`) 404'd on 99 of 161 cameras -- the wiki drops the spaces, and
 * some models are filed under their US name (`IXUS 50` -> `SD400`).
 */
export function wikiUrl(camera) {
  const title = meta && meta.wiki ? meta.wiki[camera.id] : null;

  return title ? 'https://chdk.fandom.com/wiki/' + title : null;
}

export function lineColor(line) {
  // stable, distinguishable hues per product line
  const hues = { A: 210, D: 195, G: 30, IXUS: 270, M: 345, N: 260, S: 150, SX: 10, TX: 55 };
  return `hsl(${hues[line] ?? 0} 32% 42%)`;
}
